import { watch, type Ref } from 'vue';
import { trackFlexibleDaysClick } from '@/entities/analytics';
import { buildHistoryCriteriaParams, type SearchCriteria } from '@/entities/searchCriteria';
import { SearchFormGoalName, type SearchFormGoal, type SearchFormTracking } from '@/entities/searchForm';
import type { Selection } from '@/features/searchForm/destination';

export interface SearchFormGoalsSource {
  tracking?: SearchFormTracking;
  modelValue: SearchCriteria;
}

export interface UseSearchFormGoals {
  track: (goal: SearchFormGoal) => void;
  trackSearchClick: () => void;
}

function getSearchKey(criteria: SearchCriteria): string {
  return JSON.stringify(buildHistoryCriteriaParams(criteria));
}

function addGoalContext(
  goal: SearchFormGoal,
  tracking: SearchFormTracking,
  destination: Selection | null,
): SearchFormGoal {
  const countryId = destination && destination.kind !== 'none' ? String(destination.country.id) : '';
  return {
    name: goal.name,
    params: {
      ...goal.params,
      page_type: tracking.pageType,
      product_type: tracking.productType,
      country_id: countryId,
    },
  };
}

export function useSearchFormGoals(
  source: SearchFormGoalsSource,
  emitGoal: (goal: SearchFormGoal) => void,
  isLoading: Ref<boolean>,
): UseSearchFormGoals {
  // Сравниваем ключ поиска, а не модель: при загрузке поля сами дописывают в неё справочные данные (курорты страны).
  let searchedKey = '';
  watch(
    isLoading,
    (loading) => {
      if (!loading && !searchedKey) {
        searchedKey = getSearchKey(source.modelValue);
      }
    },
    { immediate: true },
  );

  // Для горящих, ж/д и туровых SEO в задаче нет значений page_type и product_type: цель гибких дат им шлём без них,
  // остальные цели формы этим страницам не нужны.
  const track = (goal: SearchFormGoal): void => {
    if (source.tracking) {
      emitGoal(addGoalContext(goal, source.tracking, source.modelValue.destination));
      return;
    }
    if (goal.name === SearchFormGoalName.FlexibleClick) {
      trackFlexibleDaysClick(Number(goal.params.daysFlexible));
    }
  };

  const trackSearchClick = (): void => {
    const searchKey = getSearchKey(source.modelValue);
    track({ name: SearchFormGoalName.SearchClick, params: { isValueChanged: String(searchKey !== searchedKey) } });
    searchedKey = searchKey;
  };

  return { track, trackSearchClick };
}
